import { useEffect, useState } from 'react';
import { logger } from "@/utils/logger";
import { useTranslation } from 'react-i18next';
import { PageFallbackSkeleton } from "@/components/skeletons";

/** Max time to wait for translations before rendering children anyway */
const GATE_TIMEOUT_MS = 2500;

interface LocalizationReadyGateProps {
  children: React.ReactNode;
}

/**
 * Localization Ready Gate
 * Ensures all localization namespaces are loaded before rendering children
 * Only shows spinner on INITIAL load - language changes keep content visible
 * 
 * IMPORTANT: This gate NEVER changes the i18n language. It only controls
 * whether to show a skeleton or render children. If translations fail to load,
 * children render with defaultValue fallbacks, preserving user's language choice.
 * 
 * RESILIENCE: Timeout is aggressive (2.5s) to prevent blank-page deadlocks
 * in preview/dev environments. Children always render after timeout.
 */
export function LocalizationReadyGate({ children }: LocalizationReadyGateProps) {
  const { ready: commonReady } = useTranslation('common', { useSuspense: false });
  const { ready: pagesReady } = useTranslation('pages', { useSuspense: false });
  const { ready: componentsReady } = useTranslation('components', { useSuspense: false });
  const { ready: authReady } = useTranslation('auth', { useSuspense: false });
  const [timedOut, setTimedOut] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  
  const allReady = commonReady && pagesReady && componentsReady && authReady;

  // Mark as initialized once translations are first ready
  useEffect(() => {
    if (allReady && !hasInitialized) {
      setHasInitialized(true);
      logger.debug('[i18n] All namespaces ready, gate initialized');
    }
  }, [allReady, hasInitialized]);

  // Aggressive timeout: render children with defaultValue fallbacks
  useEffect(() => {
    if (hasInitialized || allReady) return;

    const timeout = setTimeout(() => {
      if (!allReady && !hasInitialized) {
        logger.warn('[i18n] Gate timeout after ' + GATE_TIMEOUT_MS + 'ms - rendering with fallbacks', {
          commonReady, pagesReady, componentsReady, authReady,
        });
        setTimedOut(true);
      }
    }, GATE_TIMEOUT_MS);

    return () => clearTimeout(timeout);
  }, [allReady, hasInitialized, commonReady, pagesReady, componentsReady, authReady]);

  // Only show skeleton on INITIAL load, not on language changes
  if (!allReady && !hasInitialized && !timedOut) {
    return <PageFallbackSkeleton fullPage />;
  }

  // After initialization or timeout, render children directly
  return <>{children}</>;
}
